import type { TaskActivity } from '@ssn/shared';
import { TASK_STATUS_META } from '@ssn/shared';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/Feedback';
import { cn } from '@/components/ui/cn';
import { formatDateTime, formatTime } from '@/lib/format';
import { StatusIcon } from './status';

/**
 * The task activity timeline (§12).
 *
 * The whole point is that the lifecycle of a task is legible without opening
 * another system: who did what, when, in order, in plain language. Entries are
 * grouped by day so a task that ran over a week does not read as one
 * undifferentiated list.
 */
export function ActivityTimeline({ activity }: { activity: readonly TaskActivity[] }) {
  if (activity.length === 0) {
    return <EmptyState title="No activity yet" description="Everything that happens to this task will appear here." />;
  }

  const days = groupByDay(activity);

  return (
    <div className="space-y-5">
      {days.map(([day, entries]) => (
        <div key={day}>
          <h3 className="mb-2.5 text-[11px] font-semibold tracking-wide text-text-subtle uppercase">{day}</h3>

          <ol className="relative space-y-0.5">
            {/* The spine, drawn behind the markers rather than per-item. */}
            <span aria-hidden className="absolute top-2 bottom-2 left-[11px] w-px bg-border" />

            {entries.map((entry) => (
              <li key={entry.id} className="relative flex gap-3 py-1.5 pl-0">
                <span
                  className={cn(
                    'relative z-10 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border',
                    entry.toStatus
                      ? 'border-border bg-surface text-text-muted'
                      : 'border-border bg-surface-sunken text-text-subtle',
                  )}
                >
                  {entry.toStatus ? (
                    <StatusIcon status={entry.toStatus} className="size-3" />
                  ) : entry.actor ? (
                    <Avatar user={entry.actor} size="xs" className="size-4 border-0" />
                  ) : (
                    <span aria-hidden className="size-1.5 rounded-full bg-text-subtle" />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <p className="text-sm text-text">{entry.summary}</p>
                    <time
                      dateTime={entry.createdAt}
                      title={formatDateTime(entry.createdAt)}
                      className="text-[11px] text-text-subtle tabular"
                    >
                      {formatTime(entry.createdAt)}
                    </time>
                  </div>

                  {entry.fromStatus && entry.toStatus && (
                    <p className="mt-0.5 text-xs text-text-subtle">
                      {TASK_STATUS_META[entry.fromStatus].label} → {TASK_STATUS_META[entry.toStatus].label}
                    </p>
                  )}

                  {entry.detail && (
                    <p className="mt-1 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-xs whitespace-pre-wrap text-text-muted">
                      {entry.detail}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

function groupByDay(activity: readonly TaskActivity[]): [string, TaskActivity[]][] {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const groups = new Map<string, TaskActivity[]>();
  for (const entry of activity) {
    const ms = Date.parse(entry.createdAt);
    const key = Number.isNaN(ms) ? 'Unknown date' : formatter.format(new Date(ms));
    const list = groups.get(key);
    if (list) list.push(entry);
    else groups.set(key, [entry]);
  }
  return [...groups.entries()];
}
